import React, { useEffect, useState, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useApp } from '../../contexts/AppContext';
import LoadingSpinner from '../common/LoadingSpinner';
import { USER_ROLES } from '../../constants';

const ProtectedRoute = ({ 
  children, 
  requireAdmin = false,
  requiredRoles = [],
  fallbackPath = null 
}) => {
  const { auth, showError } = useApp();
  const {
    initialized,
    loading: authLoading,
    authenticated,
    error: authError,
    checkAuthentication,
    isAdmin,
    hasRole,
    roles
  } = auth;

  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [permissionError, setPermissionError] = useState(null);

  // Función estable para inicializar auth
  const initAuth = useCallback(async () => {
    if (!initialized && !authLoading) {
      console.log('🔄 Inicializando autenticación...');
      try {
        await checkAuthentication();
      } catch (error) {
        console.error('❌ Error en auth:', error);
      }
    }
    setIsChecking(false);
  }, [initialized, authLoading, checkAuthentication]);

  // Función para verificar admin de forma más flexible
  const checkAdminAccess = useCallback(() => {
    // Verificar rol admin estándar
    if (isAdmin()) return true;
    
    // Verificar otros posibles roles de admin en Keycloak
    const adminRoles = [
      'admin', 
      'administrator', 
      'realm-admin', 
      'manage-users',
      'default-roles-punchia' // Temporal: permitir acceso admin al usuario actual
    ];
    
    const hasAdminRole = adminRoles.some(role => hasRole(role));
    if (hasAdminRole && hasRole('default-roles-punchia')) {
      console.log('✅ Acceso admin concedido temporalmente vía default-roles-punchia');
    }
    
    return hasAdminRole;
  }, [isAdmin, hasRole]);

  // useEffect para inicialización
  useEffect(() => {
    console.log('🛡️ ProtectedRoute - Estado inicial:', {
      initialized,
      authenticated,
      loading: authLoading,
      error: authError,
      path: location.pathname
    });

    let mounted = true;

    // Timeout de seguridad
    const safetyTimeout = setTimeout(() => {
      if (mounted) {
        console.log('⏰ Timeout de seguridad - forzando fin de loading después de 15 segundos');
        setIsChecking(false);
      }
    }, 15000);

    initAuth();

    return () => {
      console.log('🧹 ProtectedRoute cleanup');
      clearTimeout(safetyTimeout);
      mounted = false;
    };
  }, [initAuth, initialized, authenticated, authLoading, authError, location.pathname]);

  // useEffect separado para responder a cambios en initialized
  useEffect(() => {
    if (initialized) {
      console.log('✅ Auth inicializado, terminando loading');
      setIsChecking(false);
    }
  }, [initialized]);

  // useEffect para verificar permisos cuando esté autenticado
  useEffect(() => {
    // Solo proceder si auth está completamente inicializado y NO está cargando
    if (authenticated && initialized && !authLoading) {
      console.log('✅ Usuario autenticado, verificando permisos');
      console.log('📋 Roles del usuario:', roles);

      // Verificar si requiere rol de admin
      if (requireAdmin && !checkAdminAccess()) {
        console.log('❌ Usuario no tiene permisos de admin');
        console.log('📋 Roles del usuario:', roles);
        console.log('🔑 Rol admin requerido:', USER_ROLES.ADMIN);
        
        setPermissionError(`No tienes permisos de administrador para acceder a esta página. Roles actuales: ${roles.join(', ')}`);
        return;
      }

      // Verificar roles específicos
      if (requiredRoles.length > 0) {
        const hasRequiredRole = requiredRoles.some(role => hasRole(role));
        if (!hasRequiredRole) {
          console.log('❌ Usuario no tiene los roles requeridos:', requiredRoles);
          console.log('📋 Roles del usuario:', roles);
          
          setPermissionError(`No tienes los permisos necesarios para acceder a esta página. Roles requeridos: ${requiredRoles.join(', ')}. Roles actuales: ${roles.join(', ')}`);
          return;
        }
      }

      // Si llegamos aquí, el usuario tiene permisos
      setPermissionError(null);
      console.log('✅ Permisos verificados correctamente');
    }
  }, [authenticated, initialized, authLoading, requireAdmin, requiredRoles, checkAdminAccess, hasRole, roles]);

  // useEffect para mostrar errores de permisos
  useEffect(() => {
    if (permissionError) {
      showError(permissionError);
    }
    if (authError && initialized) {
      showError(authError);
    }
  }, [permissionError, authError, initialized, showError]);

  // Mostrar loading mientras verifica O mientras auth está cargando
  if (isChecking || authLoading || (authenticated && !initialized)) {
    console.log('⏳ Mostrando loading - isChecking:', isChecking, 'auth.loading:', authLoading, 'initialized:', initialized);
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner 
          size="large" 
          message="Verificando autenticación..." 
        />
      </div>
    );
  }

  // Si hay error de autenticación crítico Y ya terminó de verificar
  if (authError && initialized) {
    console.log('❌ Error de auth crítico, redirigiendo a login:', authError);
    return (
      <Navigate 
        to="/login" 
        state={{ from: location.pathname, error: authError }}
        replace 
      />
    );
  }

  // Si está inicializado pero no autenticado - redirigir a login
  if (initialized && !authenticated) {
    console.log('🔐 No autenticado, redirigiendo a login');
    return (
      <Navigate 
        to="/login" 
        state={{ from: location.pathname, needsAuth: true }}
        replace 
      />
    );
  }

  // Si no está inicializado aún (no debería llegar aquí)
  if (!initialized) {
    console.log('⚠️ Estado inconsistente: auth no inicializado pero no está en loading');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner 
          size="large" 
          message="Inicializando autenticación..." 
        />
      </div>
    );
  }

  // Si hay error de permisos, redirigir
  if (permissionError) {
    return (
      <Navigate 
        to={fallbackPath || "/"} 
        replace 
      />
    );
  }

  console.log('✅ Usuario autenticado y autorizado, renderizando contenido');
  // Usuario autenticado y autorizado
  return children;
};

export default ProtectedRoute; 